import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type BrandNameResolution, resolveBrandName } from '@/app/brands/brandCatalogActions';
import { useStorageUpload } from '@/db/useStorageUpload';
import logger from '@/util/logger';
import {
    allBrandsState,
    allEquipmentTypesState,
    equipmentCountForModelState,
    equipmentTypeFamiliesState,
    modelDocumentCandidatesState,
    modelDocumentEntriesState,
    modelDocumentSearchesState,
    modelManualsJobState,
    modelState,
} from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { ModelDao, ModelDocumentCandidateDao, ModelDocumentDao, RockManualsJobDao } from '@rock/db-admin';
import {
    type Brand,
    type BrandId,
    type EquipmentTypeId,
    isCustomId,
    type Model,
    type ModelDocTag,
    type ModelDocument,
    type ModelDocumentCandidate,
    type ModelDocumentId,
    type ModelDocumentLink,
    ModelIdSchema,
    type ModelKnownAsKind,
    type ModelParkReason,
    type TrimTarget,
} from '@rock/types';
import {
    assertCanSupersede,
    coverageAppliesToModel,
    directDocumentLink,
    hasAppVisibleDoc,
    includesAppVisibleTag,
    type ModelDocumentEntry,
    planReviewState,
    publishDocumentFromCandidate,
    supersessionRefusal,
    withDocumentScope,
    withMergedPublish,
} from '@rock/manuals';
import { type FC, useCallback, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { brandsById, modelBrandLabel } from './modelBrand';
import type { PublishScope } from './coverageDraft';
import { CustomTypeBanner } from './CustomTypeBanner';
import {
    buildCoverageRule,
    type DocumentEdit,
    newestRevision,
    publishedTags,
    replaceDocument,
    sameCoverageRule,
    saveDocumentEdit,
    unlinkDocumentFromModel,
} from './documentEdits';
import type { DocumentCut } from './documentShared';
import { analyzeDocumentLanguages, applyDocumentTrim, restoreDocumentTrim } from './manualsApi';
import { offerDocumentFile, offerDocumentUrl, requestDocumentSearch } from './documentOffers';
import { ModelDetailsTab } from './ModelDetailsTab';
import { type ModelChangeDecision, ModelChangeDocumentsDialog } from './ModelChangeDocumentsDialog';
import {
    hasModelChangeEffect,
    type ModelChangeEffect,
    type ModelIdentityChange,
    modelChangeEffect,
} from './modelChangeDocuments';
import { ModelEquipmentTab } from './ModelEquipmentTab';

const log = logger('ModelDetailsPage');

const modelDao = new ModelDao();
const modelDocumentDao = new ModelDocumentDao();
const candidateDao = new ModelDocumentCandidateDao();
const rockManualsJobDao = new RockManualsJobDao();

const ModelDetailsPage: FC = () => {
    const { modelId: rawModelId } = useParams<{ modelId: string }>();
    const modelId = useMemo(
        () => (rawModelId ? ModelIdSchema.decode(decodeURIComponent(rawModelId)) : undefined),
        [rawModelId],
    );
    const [model, modelLoadable] = useLoadableAtomValue(useMemo(() => modelState(modelId), [modelId]));
    const entries = useAsyncAtomValue(
        useMemo(() => modelDocumentEntriesState(modelId), [modelId]),
        [],
    );
    const manualsJob = useAsyncAtomValue(useMemo(() => modelManualsJobState(modelId), [modelId]));
    const candidates = useAsyncAtomValue(
        useMemo(() => modelDocumentCandidatesState(modelId), [modelId]),
        [],
    );
    const searches = useAsyncAtomValue(
        useMemo(() => modelDocumentSearchesState(modelId), [modelId]),
        [],
    );
    const [equipmentCount, equipmentCountLoadable] = useLoadableAtomValue(
        useMemo(() => equipmentCountForModelState(modelId), [modelId]),
        0,
    );
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brandLookup = useMemo(() => brandsById(brands), [brands]);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const equipmentTypes = useAsyncAtomValue(allEquipmentTypesState, []);
    const [pendingChange, setPendingChange] = useState<
        { change: ModelIdentityChange; effect: ModelChangeEffect } | undefined
    >(undefined);
    const { uploadFile, progress: uploadProgress } = useStorageUpload();
    const [saving, setSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    /**
     * Wrap a write with the shared busy flag + error toast; resolves whether it succeeded. Pass a
     * FUNCTION for `successMessage` when the message depends on what the write returned — a template
     * string would be interpolated before `work` ever runs.
     */
    const runWrite = useCallback(
        async (work: () => Promise<void>, successMessage: string | (() => string)): Promise<boolean> => {
            setSaving(true);
            try {
                await work();
                toast.success(typeof successMessage === 'function' ? successMessage() : successMessage);
                return true;
            } catch (error) {
                toast.error('Write failed', {
                    description: error instanceof Error ? error.message : String(error),
                });
                return false;
            } finally {
                setSaving(false);
            }
        },
        [],
    );

    // Keep this model's acquisition queue and revisit nudge honest after a link write (`planReviewState`),
    // at once so the page reads right. A document edit reaches every other linking model through the
    // syncDocStatusOnDocumentChange trigger, which applies the same rule.
    const syncReviewState = useCallback(
        async (nextEntries: ModelDocumentEntry[]) => {
            if (!modelId || !model) return;
            const plan = planReviewState(model, nextEntries, manualsJob);
            if (plan.clearRevisit) await modelDao.clearModelRevisit(modelId);
            if (!manualsJob) return;
            if (plan.job === 'mark-reviewed') await rockManualsJobDao.markReviewed(manualsJob.id);
            if (plan.job === 'reopen') await rockManualsJobDao.reopenForReview(manualsJob.id);
        },
        [manualsJob, model, modelId],
    );

    // Publishing IS the human's classification — a candidate's verdict never blocks it. The document id
    // is the candidate id (content hash): fresh bytes create the artifact, bytes already on file (from
    // any model) get a tag and scope UNION and this model just links them. Reads + validation come first,
    // then link, then artifact — so the ingest trigger finds a linking model for its prompt context, and a
    // validation throw can't leave a dangling link.
    //
    // `publish.linkThisModel: false` is the one path that skips the link: a manual for a DIFFERENT
    // family, found while reviewing this model. The artifact is then carried solely by its coverage rule,
    // and `ingestModelManual` reads its prompt context off that rule instead — which is why the opt-out
    // is only ever offered alongside one.
    const handlePublishCandidate = useCallback(
        (
            candidate: ModelDocumentCandidate,
            tags: ModelDocTag[],
            title: string | undefined,
            notify: boolean,
            publish: PublishScope,
        ) => {
            // Filled in by the write, read by the toast — which is why `runWrite` takes a message THUNK.
            let outcome = { linkedHere: true, ruleApplied: false };
            return runWrite(
                async () => {
                    if (!modelId || !model) throw new Error('No model loaded');
                    if (!model.brandId) throw new Error('Link a brand to this model first');
                    const now = new Date().toISOString();
                    const existing = await modelDocumentDao.getModelDocument(candidate.id);
                    const replaced = publish.replaces
                        ? await modelDocumentDao.getModelDocument(publish.replaces)
                        : undefined;
                    // The lead-kind refusal comes before any write: refused after the publish, the bytes would be
                    // live but replace nothing.
                    const refusal = replaced && supersessionRefusal({ tags: publishedTags(tags, existing) }, replaced);
                    if (refusal) throw new Error(refusal);
                    const built = publish.rule ? buildCoverageRule(publish.rule, now) : undefined;
                    // Whether the reviewer's rule is what ends up on the artifact — false when a rule
                    // already on file wins below. The toast reads this: an unlinked publish whose rule was
                    // ALSO dropped changed nothing about this model beyond the tags, and must not claim to.
                    let ruleApplied = !!built;

                    let link: ModelDocumentLink | undefined;
                    if (publish.linkThisModel) {
                        link = directDocumentLink(candidate.id, now, { notify });
                        await modelDao.linkDocument(modelId, link);
                    }
                    let document: ModelDocument;
                    if (existing) {
                        // Bytes already on file (another model published them): FIELD PATCHES only — a
                        // full-doc set from this snapshot could clobber an in-flight ingest completion.
                        // A publish only ever adds to what the document is for; the form locks what it lists.
                        const merged = withMergedPublish(existing, { tags, ...publish.scope }, now);
                        const scope = { brandIds: merged.brandIds, typeIds: merged.typeIds };
                        await modelDocumentDao.setDocumentTags(
                            existing.id,
                            merged.tags,
                            title === undefined ? existing.title : title,
                        );
                        let coverage = existing.coverage;
                        if (built && !existing.coverage) {
                            coverage = built;
                        } else if (built && !sameCoverageRule(existing.coverage, built)) {
                            // The artifact already carries a human-confirmed rule; the form's draft is a
                            // pre-seed the reviewer never saw next to it. Overwriting here would narrow
                            // the rule fleet-wide (and revoke its links) from a plain re-publish — keep
                            // the confirmed rule and point at the surface that shows it before editing.
                            ruleApplied = false;
                            toast.info(
                                'Kept the document’s existing coverage rule — use “Edit publication…” to change it',
                            );
                        }
                        const grew =
                            scope.brandIds.length > existing.brandIds.length ||
                            scope.typeIds.length > existing.typeIds.length;
                        if (grew || coverage !== existing.coverage) {
                            await modelDocumentDao.setDocumentReach(existing.id, {
                                ...scope,
                                coverage: coverage ?? null,
                            });
                        }
                        document = withTitle({ ...merged, ...scope, ...(coverage ? { coverage } : {}) }, title);
                    } else {
                        const fresh = publishDocumentFromCandidate(candidate, tags, now, { ...publish.scope, title });
                        document = built ? { ...fresh, coverage: built } : fresh;
                        await modelDocumentDao.setModelDocument(document);
                    }
                    // The candidate leaves THIS model's review list either way — it has been dealt with, and
                    // the disposition records where its bytes went even when they went somewhere else.
                    await candidateDao.setDisposition(modelId, candidate.id, 'published', document.id);
                    if (replaced) await replaceDocument(replaced, document.id);
                    // Only a link changes what this model has. Without one its acquisition job stays open
                    // for review — it still has no manual of its own, which is the truth.
                    if (link) {
                        await syncReviewState([
                            ...entries.filter((e) => e.doc.id !== document.id),
                            { link, doc: document },
                        ]);
                    }
                    outcome = { linkedHere: publish.linkThisModel, ruleApplied };
                },
                () => publishToastMessage(tags, outcome),
            );
        },
        [entries, model, modelId, runWrite, syncReviewState],
    );

    // Bytes a newer revision replaced: link the newest revision directly instead, and record where the candidate
    // went. A link already there keeps its kind and its marks.
    const handleLinkRevision = useCallback(
        (candidate: ModelDocumentCandidate, revisionId: ModelDocumentId) =>
            runWrite(async () => {
                if (!modelId || !model) throw new Error('No model loaded');
                const revision = await newestRevision(revisionId);
                if (!revision) throw new Error('The newer revision is no longer published');
                const link = model.documents?.[revision.id]
                    ? undefined
                    : directDocumentLink(revision.id, new Date().toISOString());
                if (link) await modelDao.linkDocument(modelId, link);
                await candidateDao.setDisposition(modelId, candidate.id, 'published', revision.id);
                if (link) await syncReviewState([...entries, { link, doc: revision }]);
            }, 'Linked the newer revision'),
        [entries, model, modelId, runWrite, syncReviewState],
    );

    // One save for "what is this document, what is it for, and who does it reach" (`saveDocumentEdit`);
    // the reach write wakes the syncDocumentCoverage trigger only when the reach changed.
    const handleEditPublication = useCallback(
        (doc: ModelDocument, edit: DocumentEdit) =>
            runWrite(async () => {
                const saved = await saveDocumentEdit(doc, edit);
                await syncReviewState(
                    entries.map((entry) => (entry.doc.id === doc.id ? { ...entry, doc: saved } : entry)),
                );
            }, 'Publication updated'),
        [entries, runWrite, syncReviewState],
    );

    // Unlink from THIS model; the artifact survives while other models still link it (or Phase 2
    // coverage claims it). The last link leaving deletes the artifact too — the mirror trigger then
    // removes its public bytes, restoring exactly the old unpublish semantics (the staged candidate
    // stays re-publishable either way).
    const handleUnpublish = useCallback(
        (doc: ModelDocument, alsoNotRelevant: boolean) =>
            runWrite(
                async () => {
                    if (!modelId || !model) throw new Error('No model loaded');
                    // A doc whose OWN coverage rule matches this model would re-link seconds after the
                    // unlink (the enqueue trigger applies coverage on every model write) — refusing with
                    // guidance beats a silent resurrect. Prefix rules have no per-model exclusions (yet).
                    if (coverageAppliesToModel(doc, model, families)) {
                        throw new Error(
                            'This document is applied by its own coverage rule — edit its publication (narrow the prefixes or types) instead of unpublishing.',
                        );
                    }
                    const backing = candidates.find((candidate) => candidate.id === doc.id);
                    await unlinkDocumentFromModel(modelId, doc, backing, alsoNotRelevant);
                    await syncReviewState(entries.filter((entry) => entry.doc.id !== doc.id));
                },
                alsoNotRelevant ? 'Unpublished and marked not relevant' : 'Unpublished — candidate back in review',
            ),
        [candidates, entries, families, model, modelId, runWrite, syncReviewState],
    );

    // Supersession is the "correct but outdated" archival FOR THIS MODEL: the link keeps its library
    // entry (with its replacement on record) and drops out of every user-facing read here — other
    // models' links are untouched. Unpublish stays the remedy for WRONG documents.
    const handleSupersede = useCallback(
        (doc: ModelDocument, supersededBy: ModelDocumentId | null) =>
            runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    if (supersededBy) assertCanSupersede(entries, doc.id, supersededBy);
                    await modelDao.setDocumentSupersededBy(modelId, doc.id, supersededBy);
                    await syncReviewState(
                        entries.map((entry) =>
                            entry.doc.id === doc.id
                                ? { ...entry, link: { ...entry.link, supersededBy: supersededBy ?? undefined } }
                                : entry,
                        ),
                    );
                },
                supersededBy ? 'Marked superseded — no longer served here' : 'Supersession cleared',
            ),
        [entries, modelId, runWrite, syncReviewState],
    );

    // Server-side per-page language detection on the staged candidate bytes (shared id with the published
    // doc). The result lands on the candidate doc, so badges update via the subscription; takes ~seconds.
    const handleAnalyzeLanguages = useCallback(
        (docId: ModelDocumentId) =>
            runWrite(async () => {
                if (!modelId) throw new Error('No model id');
                await analyzeDocumentLanguages({ modelId, docId });
            }, 'Language analysis complete'),
        [modelId, runWrite],
    );

    // Cut pages out of a PDF, server-side and IN PLACE — the record keeps its id, links, tags and
    // coverage, and only its bytes change. On a published document that means every model linking it
    // serves the cut immediately; on a candidate nothing user-facing moves until it's published. The
    // server resolves the ranges against the real PDF (rock-admin never counts its pages), writes the
    // bytes, and records the decision for stats.
    const handleApplyTrim = useCallback(
        (target: TrimTarget, docId: ModelDocumentId, cut: DocumentCut) => {
            // Only the server ever sees the real page count, so it reports what the cut actually did and
            // the toast states it. Lazy message: `applied` is filled in by the time it's read.
            let applied = '';
            return runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    const { keptPages, totalPages } = await applyDocumentTrim({ modelId, docId, target, ...cut });
                    applied = `Cut to ${keptPages} of ${totalPages} pages`;
                },
                () => applied,
            );
        },
        [modelId, runWrite],
    );

    // Back to the FULL pdf, discarding every cut on the record — not a one-step undo, which is why the
    // menu says "Restore full PDF". No PDF work: the original was parked under its own prefix and never
    // overwritten, so this just points the record back at it.
    const handleRestoreTrim = useCallback(
        (target: TrimTarget, docId: ModelDocumentId) =>
            runWrite(async () => {
                if (!modelId) throw new Error('No model id');
                await restoreDocumentTrim({ modelId, docId, target });
            }, 'Full PDF restored'),
        [modelId, runWrite],
    );

    const handleSetNotRelevant = useCallback(
        (candidate: ModelDocumentCandidate, notRelevant: boolean) =>
            runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    await candidateDao.setDisposition(modelId, candidate.id, notRelevant ? 'not-relevant' : undefined);
                },
                notRelevant ? 'Marked not relevant — the worker will never re-propose it' : 'Candidate restored',
            ),
        [modelId, runWrite],
    );

    // A search request carries NO dispute — "run again with current information". The onCreate trigger
    // arms the worker queue; the worker reports progress back onto the search doc.
    const handleRequestSearch = useCallback(
        (offeredUrl?: string) =>
            runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    if (offeredUrl) await offerDocumentUrl(modelId, offeredUrl);
                    else await requestDocumentSearch(modelId);
                },
                offeredUrl ? 'Link queued — Rock will fetch and verify it' : 'Search queued',
            ),
        [modelId, runWrite],
    );

    // Stop the worker from making further attempts on this model. A dozens-of-minutes hunt gets freed up so
    // you can offer a file/URL instead; an attempt already running is left to finish (the worker doesn't
    // re-read the job mid-run), but no next attempt is claimed. Re-run / an offer revives it. The in-flight
    // search isn't touched here — the Acquisition card treats a cancelled job as not-in-flight so Offer/Re-run
    // re-enable immediately, and the worker resolves that search when its current attempt ends. Writing it
    // terminally here would instead race a worker `finalize` that just succeeded and clobber a real result.
    const handleCancelJob = useCallback(
        () =>
            runWrite(async () => {
                if (!manualsJob) throw new Error('No worker job to cancel');
                await rockManualsJobDao.cancelJob(manualsJob.id);
            }, 'Job cancelled — no further attempts'),
        [manualsJob, runWrite],
    );

    // "Bring it in": an explicit offer for an oversize candidate with the staging cap waived — the worker
    // fetches it full-size and it comes back as a normal, previewable, publishable candidate.
    const handleBringIn = useCallback(
        (candidate: ModelDocumentCandidate) =>
            runWrite(async () => {
                if (!modelId) throw new Error('No model id');
                const offeredUrl = candidate.urls[0];
                if (!offeredUrl) throw new Error('Candidate has no source URL');
                await offerDocumentUrl(modelId, offeredUrl, { allowOversize: true });
            }, 'Fetching it full-size — it will appear as a reviewable candidate shortly'),
        [modelId, runWrite],
    );

    // An offered file goes through the same pipeline as an offered URL (`offerDocumentFile`): nothing
    // reaches the app until a human publishes the candidate it yields.
    const handleOfferFile = useCallback(
        async (file: File): Promise<boolean> => {
            if (!modelId) return false;
            setIsUploading(true);
            try {
                await offerDocumentFile(modelId, file, uploadFile);
                toast.success('File offered — Rock will verify it and add it to Candidates');
                return true;
            } catch (error) {
                toast.error('Unable to offer the file', {
                    description: error instanceof Error ? error.message : String(error),
                });
                return false;
            } finally {
                setIsUploading(false);
            }
        },
        [modelId, uploadFile],
    );

    // Archive = tombstone for a junk IDENTITY (mis-scan, test-device artifact): hidden from the catalog,
    // the ambient manual hunt stops, but spellings still resolve re-scans to it and equipment keeps working.
    const handleSetArchived = useCallback(
        (archived: boolean) =>
            runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    await modelDao.setModelArchived(modelId, archived);
                    // Archiving a junk identity moots a succeeded hunt's output; restoring an archived
                    // model with no app-visible doc puts that output back up for review.
                    if (archived && manualsJob?.status === 'succeeded') {
                        await rockManualsJobDao.markReviewed(manualsJob.id);
                    } else if (!archived && manualsJob?.status === 'reviewed' && !hasAppVisibleDoc(entries)) {
                        await rockManualsJobDao.reopenForReview(manualsJob.id);
                    }
                },
                archived ? 'Model archived — hidden from the catalog' : 'Model restored to the catalog',
            ),
        [entries, manualsJob, modelId, runWrite],
    );

    // Park = a standing verdict on a REAL product (reason-tagged); no confirmation, hides nothing. The
    // job is deliberately left alone: a candidate still awaiting review stays reviewable (needs-review
    // outranks parked in the rollup), so parking never buries work.
    const handleSetParked = useCallback(
        (reason: ModelParkReason | null) =>
            runWrite(
                async () => {
                    if (!modelId) throw new Error('No model id');
                    await modelDao.setModelParked(modelId, reason);
                },
                // Unparking does NOT resume a hunt: `skipReason` still refuses to re-arm a terminal job
                // on a routine model edit, so the admin's next move has to be an explicit Re-run.
                reason ? 'Model parked — the ambient hunt is off' : 'Model unparked — use Re-run search to hunt again',
            ),
        [modelId, runWrite],
    );

    const handleDismissRevisit = useCallback(
        () =>
            runWrite(async () => {
                if (!modelId) throw new Error('No model id');
                await modelDao.clearModelRevisit(modelId);
            }, 'Revisit dismissed'),
        [modelId, runWrite],
    );

    const handleConfirmKnownAs = useCallback(
        async (index: number) => {
            if (!modelId || !model?.knownAs) return;
            const knownAs = model.knownAs.map((entry, i) => (i === index ? { ...entry, confirmed: true } : entry));
            await runWrite(() => modelDao.setModelKnownAs(modelId, knownAs), 'Identity confirmed');
        },
        [model, modelId, runWrite],
    );

    const handleUnconfirmKnownAs = useCallback(
        async (index: number) => {
            if (!modelId || !model?.knownAs) return;
            const knownAs = model.knownAs.map((entry, i) => (i === index ? { ...entry, confirmed: false } : entry));
            await runWrite(() => modelDao.setModelKnownAs(modelId, knownAs), 'Identity unconfirmed');
        },
        [model, modelId, runWrite],
    );

    const handleAddKnownAs = useCallback(
        async (entry: { name: string; kind: ModelKnownAsKind }): Promise<boolean> => {
            if (!modelId || !model) return false;
            const name = entry.name.trim();
            if (!name) return false;
            // Admin-added identities are a human assertion, so they land confirmed (no evidence URL).
            const knownAs = [...(model.knownAs ?? []), { name, kind: entry.kind, confirmed: true }];
            return runWrite(() => modelDao.setModelKnownAs(modelId, knownAs), 'Identity added');
        },
        [model, modelId, runWrite],
    );

    const handleEditKnownAs = useCallback(
        async (index: number, name: string, kind: ModelKnownAsKind): Promise<boolean> => {
            if (!modelId || !model?.knownAs) return false;
            const knownAs = model.knownAs.map((entry, i) => (i === index ? { ...entry, name, kind } : entry));
            return runWrite(() => modelDao.setModelKnownAs(modelId, knownAs), 'Identity updated');
        },
        [model, modelId, runWrite],
    );

    const handleDeleteKnownAs = useCallback(
        async (index: number): Promise<boolean> => {
            if (!modelId || !model?.knownAs) return false;
            const knownAs = model.knownAs.filter((_, i) => i !== index);
            return runWrite(() => modelDao.setModelKnownAs(modelId, knownAs), 'Identity removed');
        },
        [model, modelId, runWrite],
    );

    // A type or brand change that its documents would not follow asks first (`ModelChangeDocumentsDialog`);
    // one that touches no document writes straight away.
    const changeIdentity = useCallback(
        async (change: ModelIdentityChange, write: () => Promise<void>, message: string): Promise<boolean> => {
            if (!model) return false;
            const effect = modelChangeEffect(entries, model, change, families);
            if (!hasModelChangeEffect(effect)) return runWrite(write, message);
            setPendingChange({ change, effect });
            return true;
        },
        [entries, families, model, runWrite],
    );

    // Linking a brand is authoritative (clears any worker suggestion); automation never overwrites it.
    // It also changes the worker's claim fingerprint, so a re-run re-judges previously rejected candidates.
    const handleSetBrand = useCallback(
        async (brandId: BrandId | null, lineId?: string | null): Promise<boolean> => {
            if (!modelId) return false;
            const write = () => modelDao.setModelBrand(modelId, brandId, lineId);
            const message = brandId ? 'Brand linked' : 'Brand unlinked';
            // A line under the same brand moves no document.
            if (brandId === (model?.brandId ?? null)) return runWrite(write, message);
            return changeIdentity({ kind: 'brand', brandId, lineId }, write, message);
        },
        [changeIdentity, model?.brandId, modelId, runWrite],
    );

    // A suggestion's name links through the projection, as every automated writer resolves names, so
    // "Hunter Brunswick" lands on Hunter Fan with its line; a name the projection lacks goes to the picker.
    const handleConfirmBrandSuggestion = useCallback(
        async (name: string): Promise<boolean> => {
            let resolution: BrandNameResolution;
            try {
                resolution = await resolveBrandName(name);
            } catch (err) {
                log.error('Brand lookup failed for a suggestion', { err, modelId, name });
                toast.error('Brand lookup failed', { description: err instanceof Error ? err.message : `${err}` });
                return true;
            }
            if (resolution.status !== 'brand') return false;
            await handleSetBrand(resolution.brand.id, resolution.lineId);
            return true;
        },
        [handleSetBrand, modelId],
    );

    const handleDismissBrandSuggestion = useCallback(async (): Promise<boolean> => {
        if (!modelId) return false;
        return runWrite(() => modelDao.clearBrandSuggestion(modelId), 'Suggestion dismissed');
    }, [modelId, runWrite]);

    // Re-filing is the fix for a mis-filed model: units of the right type then join it instead of minting beside it.
    const handleSetType = useCallback(
        async (typeId: EquipmentTypeId): Promise<boolean> => {
            if (!modelId) return false;
            return changeIdentity(
                { kind: 'type', typeId },
                () => modelDao.setModelType(modelId, typeId),
                'Type updated',
            );
        },
        [changeIdentity, modelId],
    );

    // The search goes last, so it runs against the model's new identity and the documents it kept.
    const handleConfirmIdentityChange = useCallback(
        async ({ choices, searchAgain }: ModelChangeDecision): Promise<boolean> => {
            if (!modelId || !pendingChange) return false;
            const { change, effect } = pendingChange;
            const now = new Date().toISOString();
            const ok = await runWrite(
                async () => {
                    for (const entry of effect.direct) {
                        if (choices.get(entry.doc.id) === 'add') {
                            const moved = withDocumentScope(entry.doc, withIdentity(entry.doc, change), now);
                            await modelDocumentDao.setDocumentReach(entry.doc.id, {
                                brandIds: moved.brandIds,
                                typeIds: moved.typeIds,
                                coverage: entry.doc.coverage ?? null,
                            });
                        } else {
                            const backing = candidates.find((candidate) => candidate.id === entry.doc.id);
                            await unlinkDocumentFromModel(modelId, entry.doc, backing, false);
                        }
                    }
                    if (change.kind === 'type') await modelDao.setModelType(modelId, change.typeId);
                    else await modelDao.setModelBrand(modelId, change.brandId, change.lineId);
                    const left = new Set([
                        ...effect.leaving.map((entry) => entry.doc.id),
                        ...effect.direct
                            .filter((entry) => choices.get(entry.doc.id) !== 'add')
                            .map((entry) => entry.doc.id),
                    ]);
                    await syncReviewState(entries.filter((entry) => !left.has(entry.doc.id)));
                    if (searchAgain) await requestDocumentSearch(modelId);
                },
                searchAgain ? 'Updated — search queued' : 'Updated',
            );
            if (ok) setPendingChange(undefined);
            return ok;
        },
        [candidates, entries, modelId, pendingChange, runWrite, syncReviewState],
    );

    const modelDisplayName = getModelDisplayName(model, brandLookup, modelId);

    return (
        <PageShell>
            <PageHeader title={model ? modelDisplayName : 'Model details'} id={modelId} backLabel="Back to models" />

            {!model || !modelId ? (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {modelLoadable.state === 'loading' ? 'Loading model details' : 'Model not found'}
                        </CardTitle>
                        <CardDescription>
                            {modelLoadable.state === 'loading'
                                ? 'We are fetching the latest information for this model.'
                                : 'We could not find a document for this model. It may have been removed or renamed.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <TabNav
                        tabs={[
                            { label: 'Details', to: `/products/models/${encodeURIComponent(modelId)}/details` },
                            {
                                label: 'Equipment',
                                to: `/products/models/${encodeURIComponent(modelId)}/equipment`,
                                count: equipmentCountLoadable.state === 'hasData' ? equipmentCount : undefined,
                            },
                        ]}
                    />
                    {isCustomId(model.typeId) && <CustomTypeBanner model={model} />}
                    {pendingChange && (
                        <ModelChangeDocumentsDialog
                            open
                            onOpenChange={(open) => !open && setPendingChange(undefined)}
                            title={describeIdentityChange(pendingChange.change, brandLookup, equipmentTypes)}
                            addition={identityAddition(pendingChange.change, brandLookup, equipmentTypes)}
                            effect={pendingChange.effect}
                            saving={saving}
                            onConfirm={handleConfirmIdentityChange}
                        />
                    )}
                    <Routes>
                        <Route index element={<Navigate replace to="./details" />} />
                        <Route path="equipment" element={<ModelEquipmentTab modelId={modelId} />} />
                        <Route
                            path="details"
                            element={
                                <ModelDetailsTab
                                    model={model}
                                    entries={entries}
                                    brands={brands}
                                    saving={saving}
                                    isUploading={isUploading}
                                    uploadProgress={uploadProgress}
                                    equipmentCount={equipmentCount}
                                    onSetArchived={handleSetArchived}
                                    onSetParked={handleSetParked}
                                    onDismissRevisit={handleDismissRevisit}
                                    onSetBrand={handleSetBrand}
                                    onConfirmBrandSuggestion={handleConfirmBrandSuggestion}
                                    onDismissBrandSuggestion={handleDismissBrandSuggestion}
                                    onSetType={handleSetType}
                                    searches={searches}
                                    manualsJob={manualsJob}
                                    candidates={candidates}
                                    onEditPublication={handleEditPublication}
                                    onUnpublish={handleUnpublish}
                                    onSupersede={handleSupersede}
                                    onAnalyzeLanguages={handleAnalyzeLanguages}
                                    onApplyTrim={handleApplyTrim}
                                    onRestoreTrim={handleRestoreTrim}
                                    onRequestSearch={handleRequestSearch}
                                    onOfferFile={handleOfferFile}
                                    onCancelJob={handleCancelJob}
                                    onPublishCandidate={handlePublishCandidate}
                                    onLinkRevision={handleLinkRevision}
                                    onSetNotRelevant={handleSetNotRelevant}
                                    onBringIn={handleBringIn}
                                    onConfirmKnownAs={handleConfirmKnownAs}
                                    onUnconfirmKnownAs={handleUnconfirmKnownAs}
                                    onEditKnownAs={handleEditKnownAs}
                                    onDeleteKnownAs={handleDeleteKnownAs}
                                    onAddKnownAs={handleAddKnownAs}
                                />
                            }
                        />
                    </Routes>
                </>
            )}
        </PageShell>
    );
};

/** Apply the dialog's title to a merged artifact: provided overrides, blank keeps the existing one. */
function withTitle(doc: ModelDocument, title: string | undefined): ModelDocument {
    return title === undefined ? doc : { ...doc, title };
}

/**
 * What the publish toast says — derived from what actually LANDED, not from what was asked for. The
 * unlinked cases need their own wording because the page the reviewer is looking at will not gain a
 * document row, and "published" alone would read as a failed write. They also split on whether the rule
 * survived: a publish that neither linked here nor applied its rule only merged tags, and saying it was
 * "published for its coverage rule" would name a rule that was in fact discarded.
 */
function publishToastMessage(tags: ModelDocTag[], outcome: { linkedHere: boolean; ruleApplied: boolean }): string {
    if (!outcome.linkedHere) {
        return outcome.ruleApplied
            ? 'Published for its coverage rule — not linked to this model'
            : 'Tags merged into the existing document — not linked to this model';
    }
    return includesAppVisibleTag(tags) ? 'Document published to the app' : 'Document confirmed to the library';
}

/** The document's brands and types with the change's new brand or type added. */
function withIdentity(doc: ModelDocument, change: ModelIdentityChange): Pick<ModelDocument, 'brandIds' | 'typeIds'> {
    if (change.kind === 'type') return { brandIds: doc.brandIds, typeIds: [...doc.typeIds, change.typeId] };
    return { brandIds: change.brandId ? [...doc.brandIds, change.brandId] : doc.brandIds, typeIds: doc.typeIds };
}

/** What a document that moves along gains, as the reviewer reads it — none when a brand is only unlinked. */
function identityAddition(
    change: ModelIdentityChange,
    brands: Map<BrandId, Brand>,
    types: { id: EquipmentTypeId; title: string }[],
): string | undefined {
    if (change.kind === 'type') return types.find((type) => type.id === change.typeId)?.title ?? change.typeId;
    if (!change.brandId) return undefined;
    const brand = brands.get(change.brandId);
    return brand?.displayName ?? brand?.canonicalName ?? change.brandId;
}

function describeIdentityChange(
    change: ModelIdentityChange,
    brands: Map<BrandId, Brand>,
    types: { id: EquipmentTypeId; title: string }[],
): string {
    if (change.kind === 'brand' && !change.brandId) return 'Unlink the brand';
    const addition = identityAddition(change, brands, types);
    return change.kind === 'type' ? `Change type to ${addition}` : `Link ${addition}`;
}

function getModelDisplayName(model: Model | undefined, brandLookup: Map<BrandId, Brand>, fallback?: string) {
    if (!model) return fallback ?? 'this model';
    const brand = modelBrandLabel(model, brandLookup);
    const modelNumber = model.modelDisplay ?? model.modelCanonical;
    if (brand && modelNumber) {
        return `${brand} ${modelNumber}`;
    }
    return modelNumber ?? brand ?? fallback ?? 'this model';
}

export default ModelDetailsPage;
